/**
 * CoWork approval card — the on-device consent surface (hybrid CoWork, D3).
 *
 * Renders the oldest pending mutation approval (from the CoWork store) as a card
 * ABOVE the chat composer, aligned with it. Writes show a per-hunk diff so the
 * user can accept only some changes. Reads the queue from the store (fed by
 * CoworkBridge) so a re-render can never drop a prompt.
 *
 * Desktop-only; returns null when there's nothing to approve.
 *
 * @module components/cowork/cowork-approval-card
 */

import { useEffect, useMemo, useState } from "react";
import { Check, FileEdit, FilePlus, Terminal, Trash2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { getDesktop, type ApprovalRequest } from "@/lib/desktop";
import { useCoworkStore } from "@/store/cowork-store";
import { cn } from "@/lib/utils";

export function CoworkApprovalCard() {
  const current = useCoworkStore((s) => s.approvals[0]);
  const [rejectedHunks, setRejectedHunks] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  // Reset hunk selection whenever a new request reaches the front.
  useEffect(() => {
    setRejectedHunks(new Set());
    setBusy(false);
  }, [current?.id]);

  if (!current) return null;

  const respond = async (approved: boolean) => {
    if (busy) return;
    setBusy(true);
    const acceptedHunks =
      approved && current.hunks
        ? current.hunks.map((h) => h.index).filter((i) => !rejectedHunks.has(i))
        : undefined;
    try {
      await getDesktop()?.cowork.respondApproval(current.id, { approved, acceptedHunks });
    } finally {
      useCoworkStore.getState().dequeueApproval(current.id);
    }
  };

  const alwaysAllow = async () => {
    await getDesktop()?.cowork.setAutonomy("auto");
    useCoworkStore.getState().setAutonomy("auto");
    await respond(true);
  };

  const someRejected = rejectedHunks.size > 0;
  const pending = useCoworkStore.getState().approvals.length;

  return (
    <div className="mb-2 overflow-hidden rounded-xl border border-warning/50 bg-background shadow-lg">
      <Header req={current} pending={pending} />
      {current.kind === "write" && current.hunks && current.hunks.length > 0 && (
        <DiffView
          req={current}
          rejected={rejectedHunks}
          onToggle={(i) =>
            setRejectedHunks((s) => {
              const next = new Set(s);
              next.has(i) ? next.delete(i) : next.add(i);
              return next;
            })
          }
        />
      )}
      {current.kind === "exec" && (
        <pre className="max-h-40 overflow-auto border-y border-border/60 bg-muted/40 px-4 py-3 font-mono text-xs text-foreground">
          {current.command}
        </pre>
      )}
      <div className="flex items-center gap-2 px-4 py-2.5">
        <Button size="sm" className="gap-1.5" disabled={busy} onClick={() => respond(true)}>
          <Check className="size-3.5" />
          {someRejected ? "Apply selected" : current.kind === "exec" ? "Run" : "Approve"}
        </Button>
        <Button size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={() => respond(false)}>
          <X className="size-3.5" /> Reject
        </Button>
        {current.kind !== "exec" && (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto text-xs text-muted-foreground"
            disabled={busy}
            onClick={alwaysAllow}
          >
            Always allow edits
          </Button>
        )}
      </div>
    </div>
  );
}

function Header({ req, pending }: { req: ApprovalRequest; pending: number }) {
  const Icon =
    req.kind === "delete" ? Trash2 : req.kind === "exec" ? Terminal : req.isEdit ? FileEdit : FilePlus;
  const title =
    req.kind === "exec"
      ? "Run a command"
      : req.kind === "delete"
        ? "Delete a file"
        : req.isEdit
          ? "Edit a file"
          : "Create a file";
  return (
    <div className="flex items-center gap-2.5 bg-warning/10 px-4 py-2.5">
      <Icon className="size-4 shrink-0 text-warning" />
      <div className="min-w-0">
        <div className="text-sm font-semibold text-foreground">
          {title}
          <span className="ml-1 font-normal text-muted-foreground">on your machine</span>
        </div>
        {req.path && <div className="truncate font-mono text-xs text-muted-foreground">{req.path}</div>}
      </div>
      {pending > 1 && (
        <span className="ml-auto shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
          +{pending - 1} more
        </span>
      )}
    </div>
  );
}

function DiffView({
  req,
  rejected,
  onToggle,
}: {
  req: ApprovalRequest;
  rejected: Set<number>;
  onToggle: (index: number) => void;
}) {
  const hunks = useMemo(() => req.hunks ?? [], [req.hunks]);
  return (
    <div className="max-h-64 overflow-auto border-y border-border/60 bg-background">
      {hunks.map((h) => {
        const off = rejected.has(h.index);
        return (
          <div key={h.index} className={cn("border-b border-border/40 last:border-b-0", off && "opacity-45")}>
            <button
              type="button"
              onClick={() => onToggle(h.index)}
              className="flex w-full items-center gap-2 bg-muted/40 px-3 py-1 text-left font-mono text-[11px] text-muted-foreground hover:bg-muted"
            >
              <span
                className={cn(
                  "grid size-3.5 place-items-center rounded-sm border",
                  off ? "border-border" : "border-success bg-success text-success-foreground",
                )}
              >
                {!off && <Check className="size-2.5" />}
              </span>
              {h.header}
              <span className="ml-auto">{off ? "skipped" : "will apply"}</span>
            </button>
            <pre className="overflow-x-auto px-3 py-1 font-mono text-xs leading-relaxed">
              {h.lines.map((line, i) => {
                const t = line[0];
                return (
                  <span
                    key={i}
                    className={cn(
                      "block",
                      t === "+" && "bg-success/10 text-success",
                      t === "-" && "bg-destructive/10 text-destructive line-through decoration-destructive/40",
                    )}
                  >
                    {line || " "}
                  </span>
                );
              })}
            </pre>
          </div>
        );
      })}
    </div>
  );
}
